/**
 * File: useBooksSetupDoneQueries.ts
 * Path: apps/admin/src/features/accounting/hooks/useBooksSetupDoneQueries.ts
 * Description: Whether every setup step of the company's books is done. The
 * menu keeps Accounting > Setup until then; after that Setup lives under
 * Settings > Books setup. False while loading, so the link never flickers away.
 * Reads the same cached data as the Setup page.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { COMPANY_PROFILE_ID } from "@idk/config";
import { accountingRepo } from "@/shared/services/accountingService";
import { booksKey, useJournals } from "@/shared/hooks/useBooksQueries";
import { setupSteps } from "../utils/booksSetupUtils";
import { useZohoChecks } from "./useZohoChecksQueries";

export const useBooksSetupDone = (enabled: boolean): boolean => {
  const profile = useQuery({
    queryKey: [...booksKey(COMPANY_PROFILE_ID), "profile"],
    queryFn: () => accountingRepo.getProfile(COMPANY_PROFILE_ID),
    enabled,
  });
  const journals = useJournals(COMPANY_PROFILE_ID, enabled);
  const checks = useZohoChecks(COMPANY_PROFILE_ID, enabled);

  return useMemo(() => {
    if (!enabled || !profile.data || !journals.data || !checks.data) return false;
    return setupSteps(profile.data, journals.data, checks.data).every((step) => step.done);
  }, [enabled, profile.data, journals.data, checks.data]);
};
